///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { ProjectGtthBomReportPopupComponent } from './bom_report/bom-report.component';
import { GtthBomSummaryWindowComponent, ProjectGtthBomSummaryPopupComponent } from './bom_summary/bom-summary.component';
import { ProjectGtthAdvancedPlanConfigPopupComponent } from './advanced_plan/advanced-config.component';
import { GtthImportNodesComponent, GtthImportNodesPopupComponent } from './import-nodes/import-nodes-modal.component';
import { GtthImportTopologyComponent, GtthImportTopologyPopupComponent } from './import-topology/import-topology-modal.component';
import { projectModalWindowsRoute } from './project-windows.route';
import { GtthAutoconnectModalComponent, GtthAutoconnectPopupComponent } from './auto-connect/autoconnect-modal.component';
import {
    GtthReportConnectedSitesComponent,
    GtthReportConnectedSitesPopupComponent
} from './reports/connected-sites/report-connected-sites.component';
import {
    GtthReportConstraintComplianceComponent,
    GtthReportConstraintCompliancePopupComponent
} from './reports/constraint-compliance/report-constraint-compliance.component';
import { GtthReportP2PInfoComponent, GtthReportP2PInfoPopupComponent } from './reports/p2p-info/report-p2p-info.component';
import { GtthReportP2MPInfoComponent, GtthReportP2MPInfoPopupComponent } from './reports/p2mp-info/report-p2mp-info.component';
import {
    GtthReportConstraintComplianceMaxHopsNodesListPopupComponent,
    GtthReportConstraintComplianceMaxLinksNodesListPopupComponent,
    GtthReportConstraintComplianceNodesListComponent
} from './reports/constraint-compliance-nodes-list/report-constraint-compliance-nodes-list.component';
import {
    GtthRequestDesignQuotationModalComponent,
    GtthRequestDesignQuotationPopupComponent
} from './request-design-quotation/request-design-quotation-modal.component';

import {
    GtthReportNodesWithInterferenceComponent,
    GtthReportNodesWithInterferencePopupComponent
} from './reports/nodes-with-interference/nodes-with-interference.component';
import { GtthDisabledEntitiesComponent, GtthDisabledEntitiesPopupComponent } from './disabled-entities/disabled-entities.component';

import { GtthNodePointerComponent } from './map-search-pointers/node-pointer.component';
import { GtthLinkPointerComponent } from './map-search-pointers/link-pointer.component';
import {
    GtthConfigRingsErrorReportComponent,
    GtthConfigRingsErrorReportPopupComponent
} from './reports/config-rings-error-report/config-rings-error-report.component';
import { GtthHouseDetectionModalComponent, GtthHouseDetectionPopupComponent } from './house-detection/house-detection-modal.component';
import { GtthProgressWindowComponent } from './progress-window/progress-window.component';
import { GtthDesignDataChartComponent } from './progress-window/design-data-chart/design-data-chart.component';
import { GtthSharedModule } from '../../shared/shared.module';
import {
    GtthReportOverloadedSectorsComponent,
    GtthReportOverloadedSectorsPopupComponent
} from './reports/overloaded-sectors-report/report-overloaded-sectors.component';
import { GtthNodeDuplicationsResolverComponent } from './import-nodes/node-duplications-resolver/node-duplications-resolver.component';
import { GtthAltitudeModePipe } from './import-nodes/node-duplications-resolver/pipes/altitude-mode.pipe';
import { GtthDuplicateTypePipe } from './import-nodes/node-duplications-resolver/pipes/duplicate-type.pipe';
import { GtthDuplicateNodeComponent } from './import-nodes/node-duplications-resolver/node-conponents/duplicate-node.component';
import { GtthOriginNodeComponent } from './import-nodes/node-duplications-resolver/node-conponents/origin-node.component';
import { GtthNameFilterPipe } from './import-nodes/node-duplications-resolver/pipes/name-filter.pipe';
import { GtthSortByDistancePipe } from './import-nodes/node-duplications-resolver/pipes/sort-by-distance.pipe';
import { GtthManualDesignPopupComponent } from './manual-design/manual-design.component';
import { GtthShareDirectivesModule } from '../../shared/shared-directives.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { GtthReactiveFormsModule } from '../../shared/reactive-forms/reactie-forms.module';
import { GtthTunnelsReportWindowComponent, ProjectGtthTunnelsReportPopupComponent } from './reports/tunnels/tunnels-report.component';
import { GtthAnIpInfoPopupComponent, GtthAnIpInfoWindowComponent } from './reports/an-ip-info/an-ip-info.component';
import { BomSummaryRecordTableComponent } from './bom_summary/summary-record-table/bom-summary-record-table.component';
import { ProblematicBuComponent } from './reports/connected-sites/problematic-bu/problematic-bu.component';
import { ContextSensitiveHelpComponent } from '../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { TabsNavComponent } from '../../shared/tabs-nav/tabs-nav.component';
import {
    SuiLoadingDirective,
    SuiNumberControlComponent,
    SuiSelectEnumControlComponent,
    SuiSvgIconComponent,
    SuiToggleControlComponent,
    SuiTooltipDirective,
    SuiTranslateEnumPipe,
    SuiTranslatePipe
} from '@siklu/sui-lib';
import { GtthDisableFormControlsDirective } from '../../shared/directives/validation-directives/disable-form-controls.directive';
import { GtthNumberInputValidationDirective } from '../../shared/directives/validation-directives/number-input.directive';

const WINDOWS_STATES = [...projectModalWindowsRoute];

@NgModule({
    imports: [
        GtthSharedModule,
        GtthShareDirectivesModule,
        RouterModule.forChild(WINDOWS_STATES),
        MatDialogModule,
        DragDropModule,
        GtthReactiveFormsModule,
        ContextSensitiveHelpComponent,
        TabsNavComponent,
        SuiSvgIconComponent,
        SuiTranslatePipe,
        SuiTooltipDirective,
        SuiToggleControlComponent,
        SuiNumberControlComponent,
        SuiLoadingDirective,
        SuiSelectEnumControlComponent,
        SuiTranslateEnumPipe,
        GtthNodePointerComponent,
        GtthLinkPointerComponent,
        GtthDisableFormControlsDirective,
        GtthNumberInputValidationDirective
    ],
    declarations: [
        ProjectGtthBomReportPopupComponent,
        ProjectGtthBomSummaryPopupComponent,
        GtthBomSummaryWindowComponent,
        BomSummaryRecordTableComponent,
        ProjectGtthAdvancedPlanConfigPopupComponent,
        GtthImportNodesComponent,
        GtthImportNodesPopupComponent,
        GtthImportTopologyComponent,
        GtthImportTopologyPopupComponent,
        GtthAutoconnectModalComponent,
        GtthAutoconnectPopupComponent,
        GtthReportConnectedSitesComponent,
        GtthReportConnectedSitesPopupComponent,
        GtthReportConstraintComplianceComponent,
        GtthReportConstraintCompliancePopupComponent,
        GtthReportP2PInfoComponent,
        GtthReportP2PInfoPopupComponent,
        GtthReportP2MPInfoComponent,
        GtthReportP2MPInfoPopupComponent,
        GtthReportConstraintComplianceNodesListComponent,
        GtthReportConstraintComplianceMaxHopsNodesListPopupComponent,
        GtthReportConstraintComplianceMaxLinksNodesListPopupComponent,
        GtthRequestDesignQuotationModalComponent,
        GtthRequestDesignQuotationPopupComponent,
        GtthReportNodesWithInterferenceComponent,
        GtthReportNodesWithInterferencePopupComponent,
        GtthDesignDataChartComponent,
        GtthDisabledEntitiesPopupComponent,
        GtthDisabledEntitiesComponent,
        GtthConfigRingsErrorReportComponent,
        GtthConfigRingsErrorReportPopupComponent,
        GtthHouseDetectionModalComponent,
        GtthHouseDetectionPopupComponent,
        GtthProgressWindowComponent,
        GtthReportOverloadedSectorsComponent,
        GtthReportOverloadedSectorsPopupComponent,
        GtthNodeDuplicationsResolverComponent,
        GtthDuplicateNodeComponent,
        GtthOriginNodeComponent,
        GtthAltitudeModePipe,
        GtthDuplicateTypePipe,
        GtthNameFilterPipe,
        GtthSortByDistancePipe,
        GtthManualDesignPopupComponent,
        ProjectGtthTunnelsReportPopupComponent,
        GtthTunnelsReportWindowComponent,
        GtthAnIpInfoWindowComponent,
        GtthAnIpInfoPopupComponent,
        ProblematicBuComponent
    ],
    exports: [GtthNodePointerComponent, GtthProgressWindowComponent]
})
export class GtthProjectWindowsModule {}
